import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {AxiosError, CancelToken} from "axios";
import {message} from "antd";
import {AnalyticsEvent} from "../../../utils/analytics";
import {createMediaRepository, getMediaRepositoryV2} from "../data/repositories";
import {
  CoverImage,
  COVER_IMAGES_QUERY_KEY,
  OfferingType,
  UploadProgress,
} from "../domain";

/**
 * Query key constants for cache invalidation
 * Must match the keys used in the fetch hooks
 */
const QUERY_KEYS = {
  session: "COACH_SESSION_OFFERINGS",
  liveEvent: "live_classes",
  product: "digital_product",
  storeService: "GET_STORE_SERVICE",
  portfolioImages: "GET_STORE_SERVICE_PORTFOLIO_IMAGES_RQ_KEY",
} as const;

/**
 * Analytics event categories
 */
const ANALYTICS_CATEGORIES = {
  session: "Service",
  liveEvent: "Live events",
  product: "Digital Products",
  storeService: "Store Service",
} as const;

/**
 * Payload type for cover image upload mutations
 */
export interface UploadCoverImagesPayload {
  data: FormData;
  offeringId: string | number;
  onProgress?: (progress: number) => void;
  cancelToken?: CancelToken;
}

/**
 * Payload type for image delete mutations
 */
export interface DeleteImagePayload {
  mediaId: string;
  offeringId: string | number;
}

/**
 * Generic hook for uploading cover images to any offering type
 */
export const useUploadCoverImages = (offeringType: OfferingType, queryKey: string) => {
  const queryClient = useQueryClient();
  const repository = createMediaRepository();

  const analyticsCategory =
    offeringType === "service"
      ? ANALYTICS_CATEGORIES.session
      : offeringType === "live-class"
      ? ANALYTICS_CATEGORIES.liveEvent
      : offeringType === "product"
      ? ANALYTICS_CATEGORIES.product
      : ANALYTICS_CATEGORIES.storeService;

  return useMutation({
    mutationFn: async (payload: UploadCoverImagesPayload) => {
      // Extract files from FormData
      const files: File[] = [];
      payload.data.forEach((value, key) => {
        if (key === "file" && value instanceof File) {
          files.push(value);
        }
      });

      const result = await repository.uploadImages({
        files,
        offeringId: payload.offeringId,
        offeringType,
        options: {
          onProgress: payload.onProgress
            ? (progress: UploadProgress) => payload.onProgress?.(progress.percent)
            : undefined,
          cancelToken: payload.cancelToken,
        },
      });

      // Return in the format expected by existing components
      return {
        data: {
          data: {
            media_files: result.media.map((m) => ({
              id: String(m.id),
              media_url: m.url,
              media_type: "image" as const,
              offering_id: m.offeringId ?? 0,
              offering_type: (m.offeringType === "live-class"
                ? "live event"
                : m.offeringType ?? "service") as "service" | "live event" | "product",
              user_id: m.userId ?? 0,
              createdAt: m.createdAt ?? "",
              updatedAt: m.updatedAt ?? "",
            })),
          },
        },
      };
    },
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent(analyticsCategory, `${analyticsCategory} cover images fail`);
    },
    onSuccess: () => {
      AnalyticsEvent(analyticsCategory, `${analyticsCategory} cover images success`);
    },
    onSettled: (_d, _e, values) => {
      // Always invalidate offering query key
      queryClient.invalidateQueries({
        queryKey: [queryKey, String(values.offeringId)],
      });
      // Also invalidate the v2 cover-images query cache
      queryClient.invalidateQueries({
        queryKey: [
          COVER_IMAGES_QUERY_KEY,
          {offeringType, offeringId: String(values.offeringId)},
        ],
      });
    },
  });
};

/**
 * Generic hook for deleting cover images from any offering type
 */
export const useDeleteCoverImage = (offeringType: OfferingType, queryKey: string) => {
  const queryClient = useQueryClient();
  const repository = createMediaRepository();

  const analyticsCategory =
    offeringType === "service"
      ? ANALYTICS_CATEGORIES.session
      : offeringType === "live-class"
      ? ANALYTICS_CATEGORIES.liveEvent
      : offeringType === "product"
      ? ANALYTICS_CATEGORIES.product
      : ANALYTICS_CATEGORIES.storeService;

  return useMutation({
    mutationFn: async (payload: DeleteImagePayload) => {
      await repository.deleteMedia({mediaId: payload.mediaId});
    },
    onError: (error: AxiosError<{message: string}>, _values, rollback) => {
      message.error(error.message);
      AnalyticsEvent(analyticsCategory, `${analyticsCategory} cover image delete fail`);
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      AnalyticsEvent(
        analyticsCategory,
        `${analyticsCategory} cover image delete success`
      );
    },
    onSettled: (_data, _error, variables) => {
      // Always invalidate offering query key
      queryClient.invalidateQueries({
        queryKey: [queryKey, String(variables.offeringId)],
      });
      // Also invalidate the v2 cover-images query cache
      queryClient.invalidateQueries({
        queryKey: [
          COVER_IMAGES_QUERY_KEY,
          {offeringType, offeringId: String(variables.offeringId)},
        ],
      });
    },
  });
};

// ============================================================================
// Offering-specific hooks (for backward compatibility)
// ============================================================================

/**
 * Hook for uploading session cover images
 */
export const useSessionUploadCoverImages = () => {
  return useUploadCoverImages("service", QUERY_KEYS.session);
};

/**
 * Hook for deleting session cover images
 */
export const useSessionCoverImagesDelete = () => {
  return useDeleteCoverImage("service", QUERY_KEYS.session);
};

/**
 * Hook for uploading live event cover images
 */
export const useLiveEventUploadCoverImages = () => {
  return useUploadCoverImages("live-class", QUERY_KEYS.liveEvent);
};

/**
 * Hook for deleting live event cover images
 */
export const useLiveEventCoverImagesDelete = () => {
  return useDeleteCoverImage("live-class", QUERY_KEYS.liveEvent);
};

/**
 * Hook for uploading product cover images
 */
export const useProductUploadCoverImages = () => {
  return useUploadCoverImages("product", QUERY_KEYS.product);
};

/**
 * Payload type for product cover image delete (backward compatible)
 */
export interface DeleteProductImagePayload {
  mediaId: string;
  productId: string | number;
}

/**
 * Hook for deleting product cover images
 * Uses productId instead of offeringId for backward compatibility
 */
export const useProductCoverImagesDelete = () => {
  const queryClient = useQueryClient();
  const repository = createMediaRepository();

  return useMutation({
    mutationFn: async (payload: DeleteProductImagePayload) => {
      await repository.deleteMedia({mediaId: payload.mediaId});
    },
    onError: (error: AxiosError<{message: string}>, _values, rollback) => {
      message.error(error.message);
      AnalyticsEvent(
        ANALYTICS_CATEGORIES.product,
        "Digital Products cover image delete fail"
      );
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      AnalyticsEvent(
        ANALYTICS_CATEGORIES.product,
        "Digital Products cover image delete success"
      );
    },
    onSettled: (_data, _error, variables) => {
      // Always invalidate offering query key
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.product, String(variables.productId)],
      });
      // Also invalidate the v2 cover-images query cache
      queryClient.invalidateQueries({
        queryKey: [
          COVER_IMAGES_QUERY_KEY,
          {offeringType: "product", offeringId: String(variables.productId)},
        ],
      });
    },
  });
};

/**
 * Hook for uploading store service cover images
 */
export const useStoreServiceUploadCoverImages = () => {
  return useUploadCoverImages("product-service", QUERY_KEYS.storeService);
};

/**
 * Payload type for store service cover image delete (backward compatible)
 */
export interface DeleteStoreServiceImagePayload {
  mediaId: string;
  storeServiceId: string | number;
}

/**
 * Hook for deleting store service cover images
 * Uses storeServiceId instead of offeringId for backward compatibility
 */
export const useStoreServiceCoverImagesDelete = () => {
  const queryClient = useQueryClient();
  const repository = createMediaRepository();

  return useMutation({
    mutationFn: async (payload: DeleteStoreServiceImagePayload) => {
      await repository.deleteMedia({mediaId: payload.mediaId});
    },
    onError: (error: AxiosError<{message: string}>, _values, rollback) => {
      message.error(error.message);
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always invalidate offering query key
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.storeService, String(variables.storeServiceId)],
      });
      // Also invalidate the v2 cover-images query cache
      queryClient.invalidateQueries({
        queryKey: [
          COVER_IMAGES_QUERY_KEY,
          {
            offeringType: "product-service",
            offeringId: String(variables.storeServiceId),
          },
        ],
      });
    },
  });
};

// ============================================================================
// Portfolio images (v2)
// ============================================================================

/**
 * Payload for portfolio image upload
 */
export interface UploadPortfolioImagesPayload {
  data: FormData;
  productServiceId: string | number;
  onProgress?: (progress: number) => void;
  cancelToken?: CancelToken;
}

/**
 * Payload for portfolio image delete
 */
export interface DeletePortfolioImagePayload {
  mediaId: string;
  storeServiceId: string | number;
}

/**
 * Hook for uploading store service portfolio images (v2)
 */
export const useStoreServiceUploadPortfolioImages = () => {
  const queryClient = useQueryClient();
  const repository = getMediaRepositoryV2();

  return useMutation({
    mutationFn: async (payload: UploadPortfolioImagesPayload) => {
      // Extract files from FormData
      const files: File[] = [];
      payload.data.forEach((value, key) => {
        if (key === "portfolio-image" && value instanceof File) {
          files.push(value);
        }
      });

      const result = await repository.uploadPortfolioImages({
        files,
        productServiceId: payload.productServiceId,
        options: {
          onProgress: payload.onProgress
            ? (progress: UploadProgress) => payload.onProgress?.(progress.percent)
            : undefined,
          cancelToken: payload.cancelToken,
        },
      });

      // Convert to legacy format for backward compatibility
      return {
        data: {
          data: {
            media_files: result.media.map((m) => ({
              id: String(m.id),
              media_url: m.url,
              media_type: m.type as "image",
              offering_id: m.offeringId ?? 0,
              offering_type: "product-service" as const,
              user_id: m.userId ?? 0,
              createdAt: m.createdAt ?? "",
              updatedAt: m.updatedAt ?? "",
            })),
          },
        },
      };
    },
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
    },
    onSettled: (_d, _e, values) => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.storeService, String(values.productServiceId)],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.portfolioImages,
            {serviceId: String(values.productServiceId)},
          ],
        }),
      ]);
    },
  });
};

/**
 * Hook for deleting store service portfolio images (v2)
 */
export const useStoreServicePortfolioImagesDelete = () => {
  const queryClient = useQueryClient();
  const repository = getMediaRepositoryV2();

  return useMutation({
    mutationFn: async (payload: DeletePortfolioImagePayload) => {
      await repository.deleteMedia({mediaId: payload.mediaId});
    },
    onError: (error: AxiosError<{message: string}>, _values, rollback) => {
      message.error(error.message);
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSettled: (_data, _error, variables) => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.storeService, String(variables.storeServiceId)],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEYS.portfolioImages,
            {serviceId: String(variables.storeServiceId)},
          ],
        }),
      ]);
    },
  });
};

// ============================================================================
// GET Cover Images (v2)
// ============================================================================

/**
 * Base hook for fetching cover images (v2 only)
 * Returns empty array when offeringId is not provided
 */
export const useGetCoverImages = (
  offeringType: Exclude<OfferingType, "portfolio">,
  offeringId?: string | number
) => {
  const repository = getMediaRepositoryV2();

  return useQuery<CoverImage[], AxiosError<{status: boolean; message: string}>>({
    queryKey: [COVER_IMAGES_QUERY_KEY, {offeringType, offeringId: String(offeringId)}],
    queryFn: () => repository.getCoverImages(offeringType, offeringId!),
    staleTime: Infinity,
    enabled: !!offeringId,
  });
};

/**
 * Hook for fetching session cover images
 */
export const useSessionCoverImages = (sessionId?: string | number) => {
  return useGetCoverImages("service", sessionId);
};

/**
 * Hook for fetching live event cover images
 */
export const useLiveEventCoverImages = (liveEventId?: string | number) => {
  return useGetCoverImages("live-class", liveEventId);
};

/**
 * Hook for fetching product cover images
 */
export const useProductCoverImages = (productId?: string | number) => {
  return useGetCoverImages("product", productId);
};

/**
 * Hook for fetching store service cover images
 */
export const useStoreServiceCoverImages = (serviceId?: string | number) => {
  return useGetCoverImages("product-service", serviceId);
};
